import { encode } from 'uqr';

/**
 * Quiet zone, in modules, around the QR symbol.
 *
 * ISO/IEC 18004 requires 4 light modules on every side; scanners use it to find the symbol edge and
 * many refuse to lock on without it. `uqr`'s own default is 1, which is NOT enough — always pass
 * this explicitly. It also matches the `margin = 4` default of `angularx-qrcode`, which this
 * replaced.
 */
export const QR_QUIET_ZONE_MODULES = 4;

/** Upper bound on the quiet zone. `uqr` builds the border with per-row `unshift`, so an unbounded
 * value is not merely silly — it allocates a matrix of `(size + 2n)²` cells and hangs the tab. */
const QR_MAX_QUIET_ZONE_MODULES = 32;

/**
 * Error correction level. 'M' (~15% recoverable) is what `angularx-qrcode` defaulted to, so the
 * error-correction budget is unchanged. `uqr` defaults to 'L' — always pass this explicitly.
 *
 * ⚠ Matching ECC and quiet zone does NOT make the symbol the same size as before. `qrcode` (the old
 * transitive dependency) ran a segment optimiser — that is what `dijkstrajs` was for — splitting the
 * otpauth URI so the uppercase base32 secret packed as alphanumeric at 11 bits per 2 chars. `uqr`
 * emits a single byte segment at 16 bits per char, so for a real 32-char Identity key roughly a
 * third of email lengths land one QR version higher (e.g. `bob@hotel.com`: v5/37 modules before,
 * v6/41 after). Both are valid and scan; the modules are just finer. Verified by enrolling both the
 * old and new codes into an authenticator and confirming identical TOTP output.
 */
const QR_ERROR_CORRECTION = 'M';

/** A QR symbol as a raw module matrix, quiet zone included. */
export interface QrCodeMatrix {
    /** `modules[y][x]`, `true` = dark. Side length is `size` in both axes. */
    readonly modules: readonly (readonly boolean[])[];
    /** Total side length in modules, INCLUDING the quiet zone on both sides. */
    readonly size: number;
    /** Side length of the symbol itself, excluding the quiet zone. Always 21 + 4n. */
    readonly symbolSize: number;
    /** Quiet-zone width in modules, per side. */
    readonly quietZone: number;
    /** QR version 1-40, i.e. `(symbolSize - 17) / 4`. */
    readonly version: number;
}

/** A QR symbol reduced to the two attributes an `<svg>` needs. */
export interface QrCode extends QrCodeMatrix {
    /** `d` attribute for a single `<path>` covering every dark module, in module units. */
    readonly d: string;
    /** `viewBox` attribute matching `size`, so one module = one user unit. */
    readonly viewBox: string;
}

/**
 * Encode `data` into a QR module matrix.
 *
 * `data` is the PAYLOAD TO ENCODE, not a URL to fetch — for 2FA enrolment it is the
 * `otpauth://totp/...` URI built by `UserManagementAuthService`.
 *
 * Returns `null` for empty input rather than throwing: the enrolment wizard binds
 * `data?.qrCodeUrl`, which is undefined while the setup request is still in flight.
 *
 * THROWS for a payload past QR capacity (`uqr` raises `RangeError: Data too long` above ~2.3 kB).
 * That is deliberate — it is a programming error, not a state this component should render around —
 * but it means a caller feeding arbitrary user text must bound the length itself, because this runs
 * inside a `computed()` and would surface during change detection.
 */
export function encodeQrMatrix(
    data: string | null | undefined,
    quietZone: number = QR_QUIET_ZONE_MODULES,
): QrCodeMatrix | null {
    if (!data) {
        return null;
    }

    // Clamped, not just floored. `Math.max(0, Math.trunc(NaN))` is NaN, and `uqr` treats a NaN
    // border as falsy and silently skips the quiet zone entirely — yielding a QR that looks fine,
    // reports NaN dimensions, and fails to scan. Infinity hangs the encoder outright.
    const border = Number.isFinite(quietZone)
        ? Math.min(QR_MAX_QUIET_ZONE_MODULES, Math.max(0, Math.trunc(quietZone)))
        : QR_QUIET_ZONE_MODULES;
    const encoded = encode(data, { ecc: QR_ERROR_CORRECTION, border });

    return {
        modules: encoded.data,
        size: encoded.size,
        symbolSize: encoded.size - border * 2,
        quietZone: border,
        version: encoded.version,
    };
}

/**
 * Flatten a module matrix into a single SVG path `d` string, one subpath per horizontal run of dark
 * modules.
 *
 * One `<path>` rather than a few thousand `<rect>`s: a version-6 symbol is 41x41 = 1681 modules, and
 * run-length merging collapses that to a couple of hundred subpaths. Coordinates are in modules, so
 * the caller sizes the symbol purely through `viewBox` + `width`/`height`.
 */
export function qrMatrixToPathData(modules: readonly (readonly boolean[])[]): string {
    const subpaths: string[] = [];

    for (let y = 0; y < modules.length; y++) {
        const row = modules[y];
        let x = 0;
        while (x < row.length) {
            if (!row[x]) {
                x++;
                continue;
            }
            const start = x;
            while (x < row.length && row[x]) {
                x++;
            }
            const run = x - start;
            subpaths.push(`M${start} ${y}h${run}v1h-${run}z`);
        }
    }

    return subpaths.join('');
}

/**
 * Encode `data` and reduce it to the `d` + `viewBox` pair a template binds. `null` for empty input.
 */
export function buildQrCode(
    data: string | null | undefined,
    quietZone: number = QR_QUIET_ZONE_MODULES,
): QrCode | null {
    const matrix = encodeQrMatrix(data, quietZone);
    if (!matrix) {
        return null;
    }

    return {
        ...matrix,
        d: qrMatrixToPathData(matrix.modules),
        viewBox: `0 0 ${matrix.size} ${matrix.size}`,
    };
}
